import React, { useEffect, useState } from 'react'
import YumTabs from '../YumTabs/YumTabs'
import YumTab from '../YumTabs/YumTab/YumTab'
import { t } from 'ttag'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { GoalWithProgress, UserCompanyDto, UserProfileDto } from '../../services/api.types'
import YumChatButton from '../../views/ChallengeChat/components/YumChatButton/YumChatButton'
import { getFullName } from '../../common/helpers/translations'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

export interface IChallengeMeProps {
  userDto: UserProfileDto
  tab: string
  goal?: GoalWithProgress
  currentRoute: Partial<Location>
  userCompanies: UserCompanyDto[]
}

export enum ChallengeTab {
  INFO = 0,
  ME = 1,
  LEADERBOARD = 2,
  RANKING = 3,
  CHAT = 4,
}

// Helper function to get the correct tab index based on visible tabs
const getVisibleTabIndex = (tab: ChallengeTab, splitRankingView: boolean, compactRankingView: boolean): number => {
  // Deprecated ranking view
  if (splitRankingView) {
    switch (tab) {
      case ChallengeTab.INFO:
        return 0
      case ChallengeTab.ME:
        return 1
      case ChallengeTab.RANKING:
      case ChallengeTab.LEADERBOARD:
        return 2
      case ChallengeTab.CHAT:
        return 3
      default:
        return 0 // Default to INFO tab
    }
  }

  switch (tab) {
    case ChallengeTab.INFO:
      return 0
    case ChallengeTab.RANKING:
    case ChallengeTab.LEADERBOARD:
      return 1
    case ChallengeTab.CHAT:
      return 2
    default:
      return 0 // Default to INFO tab
  }
}

const getChallengeTabNumber = (value: string, splitRankingView: boolean, compactRankingView: boolean) => {
  //console.log('getChallengeTabNumber', value, splitRankingView, compactRankingView)
  switch (value) {
    default:
    case 'info':
      return getVisibleTabIndex(ChallengeTab.INFO, splitRankingView, compactRankingView)
    case 'me':
      return getVisibleTabIndex(ChallengeTab.ME, splitRankingView, compactRankingView)
    case 'leaderboard':
      return getVisibleTabIndex(ChallengeTab.LEADERBOARD, splitRankingView, compactRankingView)
    case 'ranking':
      return getVisibleTabIndex(ChallengeTab.RANKING, splitRankingView, compactRankingView)
    case 'chat':
      return getVisibleTabIndex(ChallengeTab.CHAT, splitRankingView, compactRankingView)
  }
}

export default function YumChallengeTabs(props: IChallengeMeProps) {
  const { tab, goal, userDto, currentRoute, userCompanies } = props
  if (!goal) return <>Challenge not found</>

  const { participantsCount, participating, splitRankingView: splitRankingViewRaw, compactRankingView } = goal
  const splitRankingView = splitRankingViewRaw && !compactRankingView

  // With the GROUP_CHAT feature, chat lives in the dedicated Chat view — the challenge page
  // offers it as a button next to the tabs instead of a tab of its own.
  const chatAsButton = hasFeatureFlag(userCompanies, 'groupChat')

  const { userId, firstName, lastName, email } = userDto
  const name = getFullName(firstName, lastName, email)

  const [activeChallengeTab, setActiveChallengeTab] = useState(
    getChallengeTabNumber(tab, splitRankingView, compactRankingView),
  )

  useEffect(() => {
    /*setTimeout(() => {
      document.body.classList.add('show-back-btn')
      document.body.classList.add('challenge-view')
    }, 0)*/
    return function cleanup() {
      //document.body.classList.remove('show-back-btn')
      //document.body.classList.remove('challenge-view')
      document.body.classList.remove('challenge-view__chat')
    }
  }, [])

  useEffect(() => {
    const pathParts = currentRoute.pathname ? currentRoute.pathname.split('/') : []
    const newTab = pathParts[3]
    if (newTab) setActiveChallengeTab(getChallengeTabNumber(newTab, splitRankingView, compactRankingView))
  }, [currentRoute.pathname, splitRankingView, compactRankingView])

  useEffect(() => {
    if (activeChallengeTab === getVisibleTabIndex(ChallengeTab.CHAT, splitRankingView, compactRankingView)) {
      document.body.classList.add('challenge-view__chat')
    } else {
      document.body.classList.remove('challenge-view__chat')
    }
  }, [activeChallengeTab, splitRankingView])

  const redirectTab = (tab: string) => {
    store.dispatch(push(`/challenges/${goal.id}/${tab}`))
  }

  const handleTypeTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setActiveChallengeTab(value)
    // Convert the visible tab index back to the actual tab type
    let actualTab: ChallengeTab
    if (splitRankingView) {
      //actualTab = value as ChallengeTab
      switch (value) {
        case 0:
          actualTab = ChallengeTab.INFO
          break
        case 1:
          actualTab = ChallengeTab.ME
          break
        case 2:
          actualTab = ChallengeTab.LEADERBOARD
          break
        case 3:
          actualTab = ChallengeTab.CHAT
          break
        default:
          actualTab = ChallengeTab.INFO
      }
    } else {
      switch (value) {
        case 0:
          actualTab = ChallengeTab.INFO
          break
        case 1:
          //actualTab = compactRankingView ? ChallengeTab.LEADERBOARD : ChallengeTab.RANKING
          actualTab = ChallengeTab.LEADERBOARD
          break
        case 2:
          actualTab = ChallengeTab.CHAT
          break
        default:
          actualTab = ChallengeTab.INFO
      }
    }

    switch (actualTab) {
      case ChallengeTab.INFO:
        redirectTab('info')
        break
      case ChallengeTab.ME:
        redirectTab('me')
        break
      case ChallengeTab.LEADERBOARD:
        redirectTab(splitRankingView || compactRankingView ? 'leaderboard' : 'ranking')
        break
      /*case ChallengeTab.RANKING:
        redirectTab('ranking')
        break*/
      case ChallengeTab.CHAT:
        redirectTab('chat')
        break
    }
  }

  const chatTabIndex = getVisibleTabIndex(ChallengeTab.CHAT, splitRankingView, compactRankingView)
  // When chat is a button, the chat tab does not exist — deselect the tabs on the chat route.
  const tabsValue = chatAsButton && activeChallengeTab === chatTabIndex ? false : activeChallengeTab

  return (
    <div
      style={{
        paddingBottom: activeChallengeTab === chatTabIndex ? 0 : '16px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <YumTabs value={tabsValue} onChange={handleTypeTabChange} style={{ flex: 1 }}>
        <YumTab label={t`Info`} value={getVisibleTabIndex(ChallengeTab.INFO, splitRankingView, compactRankingView)} />
        {splitRankingView && (
          <YumTab label={t`Me`} value={getVisibleTabIndex(ChallengeTab.ME, splitRankingView, compactRankingView)} />
        )}
        <YumTab
          label={compactRankingView ? t`Leaderboard` : t`Ranking`}
          value={getVisibleTabIndex(
            splitRankingView || compactRankingView ? ChallengeTab.LEADERBOARD : ChallengeTab.RANKING,
            splitRankingView,
            compactRankingView,
          )}
        />
        {goal.chatEnabled && !chatAsButton && (
          <YumChatButton
            tab={chatTabIndex}
            userId={userId}
            goalId={goal.id}
            name={name}
            connect={participantsCount > 0 && participantsCount < 2000}
            participating={participating}
            currentRoute={currentRoute}
          />
        )}
      </YumTabs>
      {goal.chatEnabled && chatAsButton && (
        <YumChatButton
          userId={userId}
          goalId={goal.id}
          name={name}
          connect={participantsCount > 0 && participantsCount < 2000}
          participating={participating}
          currentRoute={currentRoute}
          to={`/chat/challenge/${goal.id}`}
        />
      )}
    </div>
  )
}
